import React, { useState, useEffect } from "react";
import { styled, useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import logo from "../../assets/Images/Clanbridge-logo-White.png";
import { VscThreeBars } from "react-icons/vsc";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import HomeIcon from '@mui/icons-material/Home';
import Announcement from '@mui/icons-material/Announcement';

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

function ResidentSideDrawar({ children }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [userName, setUserName] = useState("");

  const ResidentName = localStorage.getItem("ResidentName") || "";

  const renderMenuItem = (moduleName, icon, path, subItems = null) => {
    return (
      <ListItem disablePadding sx={{ display: "block" }}>
        <ListItemButton
          onClick={() => handleNavigation(path)}
          sx={{
            minHeight: 48,
            justifyContent: open ? "initial" : "center",
            px: 2.5,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 3 : "auto",
              justifyContent: "center",
            }}
          >
            {icon}
          </ListItemIcon>
          <ListItemText primary={moduleName} sx={{ opacity: open ? 1 : 0 }} />
          {subItems &&
            open &&
            (subItems.isOpen ? (
              <ExpandLess sx={{ mr: "-10px" }} />
            ) : (
              <ExpandMore sx={{ mr: "-10px" }} />
            ))}
        </ListItemButton>
      </ListItem>
    );
  };

  const toggleDrawer = () => {
    console.log("Hamburger clicked");
    setOpen((prevOpen) => !prevOpen);
  };

  const handleOutsideClick = (event) => {
    if (
      !isMobile && //  Only apply on desktop
      open &&
      !event.target.closest(".MuiDrawer-paper") &&
      !event.target.closest(".MuiIconButton-edgeStart")
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [open]);

  useEffect(() => {
    const storedUserName = localStorage.getItem("userName");
    if (storedUserName) {
      setUserName(storedUserName);
    }
  }, []);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const handleBackupClick = () => {
    navigate("/backup");
  };

  const handleLogout = () => {
    sessionStorage.removeItem("firstName");
    sessionStorage.removeItem("UserId");
    sessionStorage.removeItem("isLoggedIn");
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
    handleProfileClose();
  };

  const handleDrawerEnter = () => {
    if (window.innerWidth > 600) {
      setOpen(true);
    }
  };

  const handleDrawerLeave = () => {
    if (window.innerWidth > 600) {
      setOpen(false);
    }
  };

  // const handleDrawerToggle = () => {
  //   setOpen((prevOpen) => !prevOpen);
  // };

  const handleDrawerToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setOpen(false); // closes the drawer on mobile
    }
  };

  const handleMyAccountClick = () => {
    handleProfileClose();
    navigate("/change-resident-password");
  };

  const handleResidentProfile = () => {
    navigate('/resident-profile');
  };

  const isRaiseServiceTicketRoute = location.pathname === "/res-raise-ticket";

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed">
        <Toolbar sx={{ backgroundColor: "#635bff" }}>
          {!isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              className="MuiIconButton-edgeStart"
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            <img
              src={logo}
              style={{ height: "45px", margin: "px 0 0 0px" }}
              alt="logo"
            />
          </Typography>
          {/* Avatar and Menu */}
          <IconButton
            onClick={handleProfileClick}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={Boolean(anchorEl) ? "account-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl) ? "true" : undefined}
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {ResidentName ? ResidentName[0].toUpperCase() : "R"}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={Boolean(anchorEl)}
            onClose={handleProfileClose}
            onClick={handleProfileClose}
          >
            <MenuItem style={{ color: "#635bff" }}>
              Hello! {ResidentName || "Resident"}
            </MenuItem>
            <MenuItem onClick={handleResidentProfile}>User Profile</MenuItem>
            <MenuItem onClick={handleMyAccountClick}>Change Password</MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {isMobile ? (
        <MuiDrawer
          variant="temporary"
          open={open}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Improves performance on mobile
          }}
        >
          <DrawerHeader>
            <IconButton onClick={handleDrawerToggle}>
              {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List>
            {renderMenuItem(
              "Home",
              <HomeIcon style={{ fontSize: "25px", color: "#635bff" }} />,
              "/residentmenu"
            )}
            {renderMenuItem(
              "User Profile",
              <AccountCircleIcon style={{ fontSize: "25px", color: "#635bff" }} />,
              "/resident-profile"
            )}
            {renderMenuItem(
              "Raise Ticket",
              <VscThreeBars style={{ fontSize: "25px", color: "#635bff" }} />,
              "/res-raise-ticket"
            )}

            {renderMenuItem(
              "Track Ticket",
              <HowToRegIcon sx={{ color: "#635bff" }} />,
              "/res-ticket-status"
            )}

            {renderMenuItem(
              "Tiffin Service",
              <AccountBalanceWalletIcon sx={{ color: "#635bff" }} />,
              "/tiffin-service"
            )}
            {renderMenuItem(
              "Food Service",
              <AccountBalanceWalletIcon sx={{ color: "#635bff" }} />,
              "/food-service"
            )}
            {renderMenuItem(
              "Transportation",
              <LocalActivityIcon sx={{ color: "#635bff" }} />,
              "/transportation"
            )}
            {renderMenuItem(
              "Notice Board",
              <Announcement style={{ fontSize: "25px", color: "#635bff" }} />,
              "/notice-board"
            )}
          </List>
          <Divider />
        </MuiDrawer>
      ) : (
        <Drawer
          variant="permanent"
          open={open}
          onMouseEnter={handleDrawerEnter}
          onMouseLeave={handleDrawerLeave}
        >
          <DrawerHeader>
            <IconButton onClick={toggleDrawer}>
              {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List>
            {renderMenuItem(
              "Home",
              <HomeIcon style={{ fontSize: "25px", color: "#635bff" }} />,
              "/residentmenu"
            )}
            {renderMenuItem(
              "User Profile",
              <AccountCircleIcon style={{ fontSize: "25px", color: "#635bff" }} />,
              "/resident-profile"
            )}
            {renderMenuItem(
              "Raise Ticket",
              <VscThreeBars style={{ fontSize: "25px", color: "#635bff" }} />,
              "/res-raise-ticket"
            )}

            {renderMenuItem(
              "Track Ticket",
              <HowToRegIcon sx={{ color: "#635bff" }} />,
              "/res-ticket-status"
            )}

            {renderMenuItem(
              "Tiffin Service",
              <AccountBalanceWalletIcon sx={{ color: "#635bff" }} />,
              "/tiffin-service"
            )}
            {/* {renderMenuItem(
              "Food Service",
              <AccountBalanceWalletIcon sx={{ color: "#635bff" }} />,
              "/food-service"
            )} */}
            {renderMenuItem(
              "Transportation",
              <LocalActivityIcon sx={{ color: "#635bff" }} />,
              "/transportation"
            )}
            {renderMenuItem(
              "Notice Board",
              <Announcement style={{ fontSize: "25px", color: "#635bff" }} />,
              "/notice-board"
            )}
            {/* Add other items here the same way */}
          </List>
          <Divider />
        </Drawer>
      )}

      <Box component="main" sx={{ flexGrow: 1, p: 0 }}>
        <DrawerHeader />
        {children}
      </Box>
    </Box>
  );
}

export default ResidentSideDrawar;
